/**
 * Wrap a parameterized selector factory so that calling it with the same args
 * returns the SAME selector instance across renders.
 *
 * Selector factories build a fresh `createSelector` instance on every call:
 *
 *   export const projectAttributeItemsSelector = (projectId: string) =>
 *     createSelector(inputA(projectId), inputB(projectId), combiner);
 *
 * reselect (v4) has a result cache of size 1 and no cross-instance creation
 * cache, so an inline call site like
 * `useReduxSelector(projectAttributeItemsSelector(id))` builds a brand-new
 * instance every render and recomputes the derived value from a cold cache —
 * expensive on large models. Wrapping the factory here memoizes the instance by
 * its args, so the reselect cache survives across renders and across every
 * consumer that passes the same args:
 *
 *   export const projectAttributeItemsSelector = memoizeSelectorFactory(
 *     (projectId: string) =>
 *       createSelector(inputA(projectId), inputB(projectId), combiner)
 *   );
 *
 * The returned factory has the same call signature as the input, so call sites
 * are unchanged. Composed factories that are also wrapped are shared, so their
 * inner derivations compute once per state change globally rather than once per
 * consuming instance.
 *
 * The instance cache is a bounded LRU keyed on the (stringified) args. A
 * selector instance is a pure function of Redux state — its own size-1 reselect
 * cache is overwritten on the next call with different inputs — so a persistent
 * module-level cache is safe across store recreations in tests.
 */
export const memoizeSelectorFactory = <
  Args extends Array<string | null | undefined>,
  Selector
>(
  factory: (...args: Args) => Selector,
  maxSize = 256
) => {
  const cache = new Map<string, Selector>();

  return (...args: Args) => {
    const key = JSON.stringify(args);

    const cached = cache.get(key);
    if (cached !== undefined) {
      // Refresh recency: re-insert so it becomes the most-recently-used entry.
      cache.delete(key);
      cache.set(key, cached);
      return cached;
    }

    const selector = factory(...args);
    cache.set(key, selector);

    if (cache.size > maxSize) {
      // Map preserves insertion order, so the first key is least-recently-used.
      const oldestKey = cache.keys().next().value;
      if (oldestKey !== undefined) {
        cache.delete(oldestKey);
      }
    }

    return selector;
  };
};
